import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Loader2, Mail, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OtpLoginPage() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [step, setStep] = useState('email');   // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const codeRef = useRef(null);

  useEffect(() => {
    if (step === 'code' && codeRef.current) codeRef.current.focus();
  }, [step]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = async (e) => {
    if (e) e.preventDefault();
    setError('');
    const trimmed = (email || '').trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Email inválido.'); return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/otp/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || data.message || 'No pudimos enviarte el código. Intentá de nuevo en 30 segundos.');
        return;
      }
      setEmail(trimmed);
      setStep('code');
      setCooldown(60);
      toast.success('Te enviamos el código a tu email.');
    } catch (_) {
      setError('Error de red. Reintentá en un momento.');
    } finally { setLoading(false); }
  };

  const verifyCode = async (e) => {
    if (e) e.preventDefault();
    setError('');
    const c = (code || '').replace(/\D/g, '').slice(0, 6);
    if (c.length !== 6) { setError('Ingresá los 6 dígitos del código.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: c }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || data.message || 'Código inválido o vencido.');
        return;
      }
      // Persistir la sesión (mismo shape que useAuth.login usa)
      if (setSession) {
        setSession({ token: data.access_token, user: data.user });
      } else {
        // Fallback si no existe setSession en el context
        try {
          window.localStorage.setItem('auth_token', data.access_token);
          window.localStorage.setItem('auth_user', JSON.stringify(data.user));
        } catch { /* noop */ }
      }
      toast.success('¡Bienvenido/a!');
      navigate('/alumnos');
    } catch (_) {
      setError('Error de red. Reintentá en un momento.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0D1B2A] flex flex-col items-center justify-center px-4 py-12" data-testid="otp-login-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6" data-testid="otp-back-link">
          <ArrowLeft className="h-4 w-4" /> Volver al login clásico
        </Link>
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="h-5 w-5 text-[#5B9B2F]" />
            <h1 className="text-2xl font-bold text-[#0D1B2A]">Ingresá con código</h1>
          </div>
          <p className="text-slate-500 text-sm mb-6">
            {step === 'email'
              ? 'Te enviamos un código de 6 dígitos por email. Sin contraseñas.'
              : `Ingresá el código que enviamos a ${email}. Vence en 10 minutos.`}
          </p>

          {error && (
            <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2 text-sm" data-testid="otp-error">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 'email' ? (
            <form onSubmit={requestCode} className="space-y-4" data-testid="otp-request-form">
              <div>
                <Label htmlFor="otp-email" className="text-sm text-slate-700">Email</Label>
                <Input
                  id="otp-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  data-testid="otp-email-input"
                  required
                  className="mt-1 h-12"
                />
              </div>
              <Button type="submit" disabled={loading} data-testid="otp-request-submit"
                className="w-full h-12 bg-[#5B9B2F] hover:bg-[#4a8126] text-white font-semibold rounded-xl">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enviando...</> : 'Enviarme el código'}
              </Button>
            </form>
          ) : (
            <form onSubmit={verifyCode} className="space-y-4" data-testid="otp-verify-form">
              <div>
                <Label htmlFor="otp-code" className="text-sm text-slate-700">Código de 6 dígitos</Label>
                <Input
                  ref={codeRef}
                  id="otp-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  data-testid="otp-code-input"
                  className="mt-1 h-14 text-center text-2xl tracking-[0.5em] font-mono font-bold"
                />
              </div>
              <Button type="submit" disabled={loading || code.length !== 6} data-testid="otp-verify-submit"
                className="w-full h-12 bg-[#5B9B2F] hover:bg-[#4a8126] text-white font-semibold rounded-xl disabled:opacity-50">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verificando...</> : 'Ingresar'}
              </Button>
              <div className="flex items-center justify-between text-xs text-slate-500 pt-2">
                <button type="button" onClick={() => { setStep('email'); setCode(''); setError(''); }}
                  data-testid="otp-change-email"
                  className="hover:text-[#0D1B2A] underline underline-offset-2">
                  Cambiar email
                </button>
                <button type="button" disabled={cooldown > 0 || loading} onClick={requestCode}
                  data-testid="otp-resend"
                  className="hover:text-[#0D1B2A] underline underline-offset-2 disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed">
                  {cooldown > 0 ? `Reenviar en ${cooldown}s` : 'Reenviar código'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
